import { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import api from "@/lib/api";
import { toast } from "sonner";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger, DialogFooter } from "@/components/ui/dialog";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Badge } from "@/components/ui/badge";
import { Plus, Pencil, Trash2, MapPin, Building2, Sparkles, Users } from "lucide-react";

const EMPTY = {
  primary_title: "", alternative_titles: "", description: "", professional_category: "",
  required_education: "", preferred_education: "", required_licenses: "", required_years_experience: 0,
  required_skills: "", preferred_skills: "", populations_served: "", work_setting: "", location: "",
  remote_status: "On-site", employment_type: "Full-time", client: "", pay_range: "", start_date: "",
  mandatory_requirements: "", disqualifying_criteria: "",
};
const toList = (s) => (s || "").split(",").map((x) => x.trim()).filter(Boolean);
const fromList = (a) => (Array.isArray(a) ? a.join(", ") : a || "");

export default function Positions() {
  const navigate = useNavigate();
  const [positions, setPositions] = useState([]);
  const [open, setOpen] = useState(false);
  const [form, setForm] = useState(EMPTY);
  const [editId, setEditId] = useState(null);

  const load = () => api.get("/positions").then((r) => setPositions(r.data)).catch(() => {});
  useEffect(() => { load(); }, []);

  const openNew = () => { setForm(EMPTY); setEditId(null); setOpen(true); };
  const openEdit = (p) => {
    setForm({ ...EMPTY, ...p,
      alternative_titles: fromList(p.alternative_titles), required_licenses: fromList(p.required_licenses),
      required_skills: fromList(p.required_skills), preferred_skills: fromList(p.preferred_skills),
      populations_served: fromList(p.populations_served), mandatory_requirements: fromList(p.mandatory_requirements),
      disqualifying_criteria: fromList(p.disqualifying_criteria) });
    setEditId(p.id); setOpen(true);
  };

  const save = async () => {
    if (!form.primary_title) { toast.error("Primary job title is required"); return; }
    const payload = { ...form,
      required_years_experience: Number(form.required_years_experience) || 0,
      alternative_titles: toList(form.alternative_titles), required_licenses: toList(form.required_licenses),
      required_skills: toList(form.required_skills), preferred_skills: toList(form.preferred_skills),
      populations_served: toList(form.populations_served), mandatory_requirements: toList(form.mandatory_requirements),
      disqualifying_criteria: toList(form.disqualifying_criteria) };
    try {
      if (editId) await api.put(`/positions/${editId}`, { ...payload, id: editId });
      else await api.post("/positions", payload);
      toast.success(editId ? "Position updated" : "Position created");
      setOpen(false); load();
    } catch { toast.error("Failed to save position"); }
  };

  const remove = async (id) => { await api.delete(`/positions/${id}`); toast.success("Position removed"); load(); };

  const F = ({ label, k, type = "text", ph }) => (
    <div>
      <Label className="text-xs">{label}</Label>
      <Input type={type} value={form[k]} placeholder={ph} data-testid={`pos-${k}`}
        onChange={(e) => setForm({ ...form, [k]: e.target.value })} className="mt-1" />
    </div>
  );

  return (
    <div className="p-8 max-w-7xl">
      <div className="flex items-start justify-between mb-8 fade-up">
        <div>
          <div className="text-[11px] uppercase tracking-[0.18em] text-[hsl(var(--teal))] font-semibold">Open Roles</div>
          <h1 className="font-head text-3xl font-semibold tracking-tight mt-1.5">Positions</h1>
          <p className="text-muted-foreground text-sm mt-1.5">Define the requirements the AI scores every candidate against.</p>
        </div>
        <Dialog open={open} onOpenChange={setOpen}>
          <Button onClick={() => navigate("/positions/new")} data-testid="new-position-btn" className="bg-[hsl(var(--teal))] hover:bg-[hsl(var(--teal))]/90 shadow-card"><Sparkles size={16} className="mr-1.5" /> New Position (AI)</Button>
          <DialogContent className="max-w-3xl max-h-[85vh] overflow-y-auto">
            <DialogHeader><DialogTitle className="font-head">{editId ? "Edit Position" : "New Position"}</DialogTitle></DialogHeader>
            <div className="space-y-5">
              <section className="grid md:grid-cols-2 gap-3">
                <F label="Primary job title *" k="primary_title" ph="e.g. Occupational Therapist" />
                <F label="Alternative titles (comma-separated)" k="alternative_titles" ph="OT, Rehab Therapist" />
                <F label="Professional category" k="professional_category" ph="Allied Health" />
                <F label="Client" k="client" />
              </section>
              <div>
                <Label className="text-xs">Job description</Label>
                <Textarea value={form.description} data-testid="pos-description" onChange={(e) => setForm({ ...form, description: e.target.value })} className="mt-1" rows={3} />
              </div>
              <section className="grid md:grid-cols-2 gap-3">
                <F label="Required education" k="required_education" ph="Master's in OT" />
                <F label="Preferred education" k="preferred_education" />
                <F label="Required licenses/certs (comma-separated)" k="required_licenses" ph="State OT License, CPR" />
                <F label="Required years experience" k="required_years_experience" type="number" />
              </section>
              <section className="grid md:grid-cols-2 gap-3">
                <F label="Required skills (comma-separated)" k="required_skills" />
                <F label="Preferred skills (comma-separated)" k="preferred_skills" />
                <F label="Populations served (comma-separated)" k="populations_served" ph="Pediatric, Geriatric" />
                <F label="Work setting" k="work_setting" ph="Outpatient Clinic" />
              </section>
              <section className="grid md:grid-cols-3 gap-3">
                <F label="Location" k="location" ph="Atlanta, GA" />
                <div>
                  <Label className="text-xs">Remote status</Label>
                  <Select value={form.remote_status} onValueChange={(v) => setForm({ ...form, remote_status: v })}>
                    <SelectTrigger className="mt-1" data-testid="pos-remote"><SelectValue /></SelectTrigger>
                    <SelectContent>{["On-site", "Remote", "Hybrid"].map((o) => <SelectItem key={o} value={o}>{o}</SelectItem>)}</SelectContent>
                  </Select>
                </div>
                <div>
                  <Label className="text-xs">Employment type</Label>
                  <Select value={form.employment_type} onValueChange={(v) => setForm({ ...form, employment_type: v })}>
                    <SelectTrigger className="mt-1" data-testid="pos-employment"><SelectValue /></SelectTrigger>
                    <SelectContent>{["Full-time", "Part-time", "Contract", "Per Diem", "Temp"].map((o) => <SelectItem key={o} value={o}>{o}</SelectItem>)}</SelectContent>
                  </Select>
                </div>
                <F label="Pay range" k="pay_range" ph="$80k–$95k" />
                <F label="Start date" k="start_date" ph="Immediate" />
              </section>
              <section className="grid md:grid-cols-2 gap-3">
                <F label="Mandatory requirements (comma-separated)" k="mandatory_requirements" />
                <F label="Disqualifying criteria (comma-separated)" k="disqualifying_criteria" />
              </section>
            </div>
            <DialogFooter>
              <Button variant="outline" onClick={() => setOpen(false)}>Cancel</Button>
              <Button onClick={save} data-testid="save-position-btn" className="bg-[hsl(var(--teal))] hover:bg-[hsl(var(--teal))]/90">Save Position</Button>
            </DialogFooter>
          </DialogContent>
        </Dialog>
      </div>

      {positions.length === 0 ? (
        <div className="text-center py-20 border border-dashed border-border rounded-lg bg-card">
          <Building2 size={30} className="mx-auto text-muted-foreground/50" />
          <p className="text-muted-foreground mt-3 text-sm">No positions yet. Create your first open role.</p>
        </div>
      ) : (
        <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-4">
          {positions.map((p, i) => (
            <div key={p.id} data-testid={`position-card-${p.id}`} onClick={() => navigate(`/positions/${p.id}`)}
              className="group card-hover bg-card border border-border rounded-lg shadow-card p-5 fade-up cursor-pointer flex flex-col" style={{ animationDelay: `${i * 30}ms` }}>
              <div className="flex items-start justify-between gap-2">
                <div className="min-w-0">
                  <h3 className="font-head font-semibold text-base leading-snug group-hover:text-[hsl(var(--teal))] transition-colors">{p.primary_title}</h3>
                  {p.professional_category && <div className="mt-2"><span className="inline-flex items-center rounded-full bg-[hsl(var(--teal))]/8 text-[hsl(var(--teal))] px-2 py-0.5 text-[11px] font-medium">{p.professional_category}</span></div>}
                </div>
                <div className="flex gap-0.5 shrink-0 opacity-0 group-hover:opacity-100 transition-opacity" onClick={(e) => e.stopPropagation()}>
                  <button onClick={() => openEdit(p)} data-testid={`edit-pos-${p.id}`} className="p-1.5 rounded-md hover:bg-secondary transition-colors text-muted-foreground hover:text-foreground"><Pencil size={14} /></button>
                  <button onClick={() => remove(p.id)} data-testid={`delete-pos-${p.id}`} className="p-1.5 rounded-md hover:bg-destructive/10 text-muted-foreground hover:text-destructive transition-colors"><Trash2 size={14} /></button>
                </div>
              </div>
              <div className="mt-4 space-y-1.5 text-sm text-muted-foreground flex-1">
                {p.client && <div className="flex items-center gap-2"><Building2 size={14} className="shrink-0" /> <span className="truncate">{p.client}</span></div>}
                {p.location && <div className="flex items-center gap-2"><MapPin size={14} className="shrink-0" /> <span className="truncate">{p.location} · {p.remote_status}</span></div>}
                {p.required_education && <div className="text-xs pt-1"><span className="font-medium text-foreground">Edu:</span> {p.required_education}</div>}
                {p.required_licenses?.length > 0 && <div className="text-xs"><span className="font-medium text-foreground">License:</span> {p.required_licenses.join(", ")}</div>}
              </div>
              <div onClick={(e) => e.stopPropagation()}>
                <button onClick={() => navigate(`/positions/${p.id}`)} data-testid={`view-matches-${p.id}`}
                  className="mt-4 w-full text-sm font-medium text-[hsl(var(--teal))] border border-[hsl(var(--teal))]/25 rounded-md py-2 hover:bg-[hsl(var(--teal))]/8 transition-colors flex items-center justify-center gap-1.5">
                  <Users size={15} /> View candidate matches
                </button>
              </div>
            </div>
          ))}
        </div>
      )}
    </div>
  );
}
